import { useCallback, useState } from 'react';
import { useMutation } from 'react-apollo';
import { ReactStripeElements } from 'react-stripe-elements';
import gql from 'graphql-tag';
import Honeybadger from '@honeybadger-io/js';

import { showErrorToast, showSuccessToast } from '../../../utilities/notificationUtils';
import Invoice from '../types/invoice';
import { INVOICES_QUERY } from './use-invoices';
import { TOTAL_BALANCE_QUERY } from './use-total-balance';

const DEFAULT_ERROR_MESSAGE =
  "We're sorry but we could not process your payment. Please try again.";

const PAY_INVOICE_MUTATION = gql`
  mutation payInvoice($invoiceId: Int!, $stripeTokenId: String!) {
    payInvoice(invoiceId: $invoiceId, stripeTokenId: $stripeTokenId) {
      invoice {
        id
      }
    }
  }
`;

const createStripeTokenId = async (
  stripe: ReactStripeElements.StripeProps,
  data: PayInvoiceData
) => {
  const tokenResponse = await stripe.createToken({ name: data.cardHolderName });
  if (tokenResponse.error?.message) {
    Honeybadger.notify(tokenResponse.error, 'Invoice Pay: stripe token creation failed');
    throw new Error(tokenResponse.error.message);
  }

  return tokenResponse.token?.id ?? '';
};

const usePayInvoice = (stripe: ReactStripeElements.StripeProps | null): UsePayInvoiceResponse => {
  const [invoiceToPay, setInvoiceToPay] = useState<Invoice>();
  const [payInvoiceMutation] = useMutation<any, PayInvoiceMutationVariables>(PAY_INVOICE_MUTATION, {
    refetchQueries: [{ query: INVOICES_QUERY }, { query: TOTAL_BALANCE_QUERY }]
  });

  const closeModal = useCallback(() => setInvoiceToPay(undefined), [setInvoiceToPay]);

  const payInvoice = useCallback(
    async (data: PayInvoiceData) => {
      try {
        if (!stripe || !invoiceToPay) {
          throw new Error(DEFAULT_ERROR_MESSAGE);
        }

        const stripeTokenId = await createStripeTokenId(stripe, data);
        const invoiceId = parseInt(invoiceToPay.id);
        await payInvoiceMutation({ variables: { invoiceId, stripeTokenId } });

        showSuccessToast("You've successfully made your payment!");
        closeModal();
      } catch (error) {
        Honeybadger.notify(error);
        const graphQLError = error.graphQLErrors?.length > 0 && error.graphQLErrors[0].message;
        showErrorToast(graphQLError || error.message || DEFAULT_ERROR_MESSAGE);
      }
    },
    [invoiceToPay, payInvoiceMutation, closeModal]
  );

  return { invoiceToPay, setInvoiceToPay, payInvoice, closeModal, isModalOpen: !!invoiceToPay };
};

export default usePayInvoice;

interface PayInvoiceData {
  cardHolderName: string;
}

interface UsePayInvoiceResponse {
  invoiceToPay?: Invoice;
  setInvoiceToPay: (invoice: Invoice) => void;
  payInvoice: (data: PayInvoiceData) => Promise<void>;
  closeModal: () => void;
  isModalOpen: boolean;
}

interface PayInvoiceMutationVariables {
  invoiceId: number;
  stripeTokenId: string;
}
